import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, Target, BookOpen, Users, Award, TrendingUp } from 'lucide-react';

const ProfessionalMethodSection = () => {
  const benefits = [
    {
      icon: Target,
      title: "Atendimento Especializado",
      description: "Técnicas avançadas para consultorias e atendimentos clínicos em aleitamento materno com precisão e eficácia."
    },
    {
      icon: BookOpen,
      title: "Base Científica Sólida",
      description: "Fundamentação teórica robusta baseada em evidências científicas e melhores práticas internacionais."
    },
    {
      icon: Users,
      title: "Metodologia Comprovada",
      description: "Método PPS aplicado em milhares de casos reais, com resultados documentados e mensuráveis."
    },
    {
      icon: Award,
      title: "Certificação Profissional",
      description: "Certificado que comprova sua especialização e eleva sua credibilidade no mercado."
    },
    {
      icon: TrendingUp,
      title: "Diferencial Competitivo",
      description: "Torne-se referência na sua área com conhecimento especializado e técnicas exclusivas."
    }
  ];

  const targetProfessionals = [
    "Enfermeiras",
    "Odontopediatras",
    "Doulas",
    "Fonoaudiólogas",
    "Pediatras e Neonatologistas",
    "Nutricionistas Materno-Infantil",
    "Fisioterapeutas Pélvicas",
    "Psicólogas Perinatais",
    "Ginecologistas Obstetras"
  ];

  return (
    <section id="method" data-section="method" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Por Que Escolher Nosso Curso de Aperfeiçoamento?
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto">
            Desenvolvido especificamente para profissionais que desejam se aperfeiçoar 
            em aleitamento materno e oferecer o melhor atendimento às suas pacientes.
          </p>
        </div>

        {/* Timeline Vertical */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="relative">
            {/* Linha vertical da timeline */}
            <div className="absolute left-4 md:left-8 top-8 bottom-8 w-0.5 bg-gradient-to-b from-primary via-accent to-secondary"></div>
            
            <div className="space-y-8">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <div key={index} className="relative flex items-start">
                    {/* Ponto da timeline */}
                    <div className="relative z-10 flex-shrink-0">
                      <div className="w-8 h-8 md:w-16 md:h-16 rounded-full bg-white border-4 border-primary flex items-center justify-center shadow-lg">
                        <IconComponent className="h-4 w-4 md:h-8 md:w-8 text-primary" />
                      </div>
                    </div>
                    
                    {/* Conteúdo do card */}
                    <div className="ml-6 md:ml-8 flex-1">
                      <Card className="border-none shadow-lg bg-white rounded-xl">
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <div className="flex-1">
                              <h3 className="text-xl font-bold text-gray-900 mb-3">
                                {benefit.title}
                              </h3>
                              <p className="text-gray-600 leading-relaxed">
                                {benefit.description}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Target Professionals */}
        <div className="bg-white rounded-2xl p-8 shadow-lg">
          <div className="text-center mb-8">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Ideal Para Profissionais:
            </h3>
            <p className="text-lg text-gray-600">
              Seja você iniciante ou experiente, nosso curso elevará seu nível de aperfeiçoamento
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8">
            {targetProfessionals.map((professional, index) => (
              <div key={index} className="flex items-center gap-3 border-b border-gray-100 py-3">
                <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                <span className="text-gray-700 font-medium">{professional}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Results Section */}
        <div className="mt-16 text-center">
          <div className="bg-primary-dark rounded-2xl p-8 text-white">
            <h3 className="text-2xl md:text-3xl font-bold mb-6">
              Números Que Comprovam Nossa Excelência
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div>
                <div className="text-3xl font-bold text-accent-light mb-2">2.000+</div>
                <p className="text-white/90">Profissionais Formados</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-accent-light mb-2">15.000+</div>
                <p className="text-white/90">Mães Assistidas</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-accent-light mb-2">95%</div>
                <p className="text-white/90">Taxa de Sucesso</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-accent-light mb-2">4.9/5</div>
                <p className="text-white/90">Avaliação Média</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProfessionalMethodSection;